import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatDateTime } from '../../shared/dateTimeFormatting';
import { tenantApi, type RosterRemovalResult, type RotationSlotPreview } from '../../services/tenantApi';
import { useAppDialog } from './AppDialog';
import { parseDateTimeLocalInTimezone, toDateTimeLocalInTimezone } from './scheduleTimezone';

export const ROSTER_REMOVAL_REASON_MAX = 200;
// Matches the API tolerance (5 minutes) with headroom for clock skew between preview and confirm.
const PAST_TOLERANCE_MS = 60_000;

export type RosterRemovalEffectiveMode = 'nextSlot' | 'now' | 'custom';

export type RosterRemovalPreviewRow = {
  startUtc: string;
  endUtc: string;
  currentUserId: string | null;
  afterUserId: string;
  changed: boolean;
};

/** Pairs each post-change slot with whoever the current roster would have had on call at that point. */
export function buildRosterRemovalPreviewRows(
  before: RotationSlotPreview[],
  after: RotationSlotPreview[],
  effectiveFromUtc: string,
): RosterRemovalPreviewRow[] {
  const effectiveMs = Date.parse(effectiveFromUtc);
  const beforeRanges = (before || []).map(b => ({ userId: b.userId, start: Date.parse(b.startUtc), end: Date.parse(b.endUtc) }));
  return (after || []).map(slot => {
    const startMs = Date.parse(slot.startUtc);
    const probe = Number.isFinite(effectiveMs) && startMs < effectiveMs ? effectiveMs : startMs;
    const current = beforeRanges.find(b => b.start <= probe && probe < b.end)?.userId ?? null;
    return {
      startUtc: slot.startUtc,
      endUtc: slot.endUtc,
      currentUserId: current,
      afterUserId: slot.userId,
      changed: !sameId(current, slot.userId),
    };
  });
}

/** Start of the member's next slot that begins after `now`, from the unchanged ("before") projection. */
export function findNextMemberSlotStart(before: RotationSlotPreview[], userId: string, now: Date = new Date()): string | null {
  const nowMs = now.getTime();
  const next = (before || [])
    .filter(s => sameId(s.userId, userId) && Date.parse(s.startUtc) > nowMs)
    .sort((a, b) => Date.parse(a.startUtc) - Date.parse(b.startUtc))[0];
  return next?.startUtc ?? null;
}

export function validateRosterRemoval(input: {
  mode: RosterRemovalEffectiveMode;
  customValue: string;
  timezone: string;
  nextSlotUtc: string | null;
  reason: string;
  now?: Date;
}): { effectiveFromUtc?: string; errors: { effective?: string; reason?: string } } {
  const errors: { effective?: string; reason?: string } = {};
  const now = input.now ?? new Date();
  let effectiveFromUtc: string | undefined;

  if (input.mode === 'nextSlot') {
    if (input.nextSlotUtc) effectiveFromUtc = input.nextSlotUtc;
    else errors.effective = 'This member has no upcoming slot. Choose Now or a custom date.';
  } else if (input.mode === 'custom') {
    const parsed = parseDateTimeLocalInTimezone(input.customValue, input.timezone);
    if (Number.isNaN(parsed.getTime())) errors.effective = 'Enter a valid effective date and time.';
    else if (parsed.getTime() < now.getTime() - PAST_TOLERANCE_MS) errors.effective = 'The effective date cannot be in the past.';
    else effectiveFromUtc = parsed.toISOString();
  }

  const reason = input.reason.trim();
  if (!reason) errors.reason = 'A reason is required.';
  else if (reason.length > ROSTER_REMOVAL_REASON_MAX) errors.reason = `Reason must be ${ROSTER_REMOVAL_REASON_MAX} characters or fewer.`;

  return { effectiveFromUtc, errors };
}

export function describeRosterRemovalError(err: unknown, fallback: string): string {
  const message = err instanceof Error ? err.message : String(err || '');
  if (/\(403\)/.test(message)) {
    return 'You do not have permission to remove members from this rotation. Only schedule managers or the rotation\'s team leads can do this.';
  }
  return message || fallback;
}

function sameId(a?: string | null, b?: string | null): boolean {
  return Boolean(a && b && a.toLowerCase() === b.toLowerCase());
}

export const RosterRemovalModal: React.FC<{
  scheduleId: string;
  timezone: string;
  rotationId: string;
  rotationName: string;
  member: { userId: string; label: string };
  resolveUserLabel: (userId: string | null | undefined) => string;
  onClose: () => void;
  onRemoved: (result: RosterRemovalResult) => void | Promise<void>;
}> = ({ scheduleId, timezone, rotationId, rotationName, member, resolveUserLabel, onClose, onRemoved }) => {
  const { confirm, dialog } = useAppDialog();
  const tz = timezone || 'UTC';
  const [mode, setMode] = useState<RosterRemovalEffectiveMode>('nextSlot');
  const [customValue, setCustomValue] = useState(() => toDateTimeLocalInTimezone(new Date(Date.now() + 60 * 60 * 1000).toISOString(), tz));
  const [reason, setReason] = useState('');
  const [nextSlotUtc, setNextSlotUtc] = useState<string | null>(null);
  const [initialised, setInitialised] = useState(false);
  const [preview, setPreview] = useState<RosterRemovalResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState(false);
  const requestSeq = useRef(0);
  const initialNowPreviewReady = useRef(false);

  const formatInTz = (iso: string) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return iso;
    return formatDateTime(date, { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // Initial preview from "now" gives the unchanged projection used to find the member's next slot.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const initial = await tenantApi.removeScheduleRosterMember(scheduleId, rotationId, { userId: member.userId, preview: true });
        if (cancelled) return;
        const next = findNextMemberSlotStart(initial.before, member.userId);
        setNextSlotUtc(next);
        if (!next) {
          initialNowPreviewReady.current = true;
          setMode('now');
          setPreview(initial);
        }
      } catch (err) {
        if (!cancelled) setError(describeRosterRemovalError(err, 'Failed to load roster preview'));
      } finally {
        if (!cancelled) setInitialised(true);
      }
    })();
    return () => { cancelled = true; };
  }, [scheduleId, rotationId, member.userId]);

  const validation = validateRosterRemoval({ mode, customValue, timezone: tz, nextSlotUtc, reason });
  const previewKey = mode === 'now' ? 'now' : validation.errors.effective ? '' : validation.effectiveFromUtc || '';

  useEffect(() => {
    if (!initialised || !previewKey) return;
    if (previewKey === 'now' && initialNowPreviewReady.current) {
      initialNowPreviewReady.current = false;
      return;
    }
    initialNowPreviewReady.current = false;
    const seq = ++requestSeq.current;
    setPreviewLoading(true);
    setError(null);
    tenantApi.removeScheduleRosterMember(scheduleId, rotationId, {
      userId: member.userId,
      effectiveFromUtc: mode === 'now' ? undefined : previewKey,
      preview: true,
    }).then(result => {
      if (seq === requestSeq.current) setPreview(result);
    }).catch(err => {
      if (seq === requestSeq.current) {
        setPreview(null);
        setError(describeRosterRemovalError(err, 'Failed to load roster preview'));
      }
    }).finally(() => {
      if (seq === requestSeq.current) setPreviewLoading(false);
    });
  }, [initialised, previewKey, scheduleId, rotationId, member.userId]);

  const rows = useMemo(
    () => (preview ? buildRosterRemovalPreviewRows(preview.before, preview.after, preview.effectiveFromUtc) : []),
    [preview],
  );
  const handoverLabel = preview?.handoverUserId ? resolveUserLabel(preview.handoverUserId) : null;
  const leavesEmpty = preview != null && preview.remainingMembers === 0;
  const canSubmit = initialised && !submitting && !previewLoading;

  const submit = async () => {
    setTouched(true);
    if (Object.keys(validation.errors).length > 0) return;
    const whenLabel = mode === 'now' ? 'immediately' : `from ${formatInTz(validation.effectiveFromUtc!)} (${tz})`;
    const ok = await confirm(
      `Remove ${member.label} from ${rotationName} ${whenLabel}? Earlier shifts are not changed. Affected swaps are cancelled and the people involved are notified.`,
      { title: 'Remove from roster', confirmLabel: 'Remove', danger: true },
    );
    if (!ok) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await tenantApi.removeScheduleRosterMember(scheduleId, rotationId, {
        userId: member.userId,
        effectiveFromUtc: mode === 'now' ? undefined : validation.effectiveFromUtc,
        reason: reason.trim(),
        preview: false,
      });
      await onRemoved(result);
    } catch (err) {
      setError(describeRosterRemovalError(err, 'Failed to remove roster member'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal modal-roster-removal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="roster-removal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="roster-removal-title">Remove from roster: {rotationName}</h3>

        <div className="form-group">
          <label htmlFor="roster-removal-member">Member</label>
          <input id="roster-removal-member" type="text" value={member.label} readOnly />
        </div>

        <fieldset className="form-group" style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={{ fontWeight: 600, marginBottom: 4 }}>Effective from ({tz})</legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="radio" name="roster-removal-mode" value="nextSlot" checked={mode === 'nextSlot'} disabled={!nextSlotUtc} onChange={() => setMode('nextSlot')} />
              Start of next slot{nextSlotUtc ? ` (${formatInTz(nextSlotUtc)})` : ' (none upcoming)'}
            </label>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="radio" name="roster-removal-mode" value="now" checked={mode === 'now'} onChange={() => setMode('now')} />
              Now
            </label>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
              <input type="radio" name="roster-removal-mode" value="custom" checked={mode === 'custom'} onChange={() => setMode('custom')} />
              Custom
            </label>
          </div>
          {mode === 'custom' && (
            <input
              aria-label="Custom effective date and time"
              type="datetime-local"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              style={{ marginTop: 6 }}
            />
          )}
          {validation.errors.effective && (mode === 'custom' || touched) && (
            <div className="error-text" role="alert">{validation.errors.effective}</div>
          )}
        </fieldset>

        <div className="form-group">
          <label htmlFor="roster-removal-reason">Reason</label>
          <textarea
            id="roster-removal-reason"
            value={reason}
            maxLength={ROSTER_REMOVAL_REASON_MAX}
            rows={2}
            placeholder="For example: left the business"
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          <div className="help-text">{reason.trim().length}/{ROSTER_REMOVAL_REASON_MAX}</div>
          {validation.errors.reason && touched && <div className="error-text" role="alert">{validation.errors.reason}</div>}
        </div>

        <h4 style={{ margin: '0.75rem 0 0.25rem' }}>Preview (next {rows.length || 12} slots)</h4>
        {!initialised || previewLoading ? (
          <div className="help-text">Loading preview…</div>
        ) : rows.length > 0 ? (
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table roster-removal-preview">
              <thead>
                <tr><th>Slot ({tz})</th><th>Current</th><th>After change</th></tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr
                    key={`${row.startUtc}|${row.afterUserId}`}
                    className={row.changed ? 'preview-row-changed' : undefined}
                  >
                    <td>{formatInTz(row.startUtc)} – {formatInTz(row.endUtc)}</td>
                    <td>{row.currentUserId ? resolveUserLabel(row.currentUserId) : '—'}</td>
                    <td>{resolveUserLabel(row.afterUserId)}{row.changed ? ' ●' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : leavesEmpty ? null : (
          <div className="help-text">No upcoming slots to preview.</div>
        )}
        {leavesEmpty && (
          <div className="error-banner" role="alert" style={{ marginTop: 8 }}>
            {member.label} is the only member. Removing them leaves a coverage gap in {rotationName}.
          </div>
        )}
        {handoverLabel && !leavesEmpty && (
          <div className="help-text" style={{ marginTop: 6 }}>Handover at the effective time: <strong>{handoverLabel}</strong> takes over.</div>
        )}

        {error && <div className="error-banner" role="alert" style={{ marginTop: 8 }}>{error}</div>}

        <div className="form-actions" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button type="button" className="btn-danger" onClick={submit} disabled={!canSubmit}>
            {submitting ? 'Removing…' : 'Remove from roster'}
          </button>
        </div>
      </div>
    </div>
    {dialog}
    </>
  );
};
